import * as RadixTooltip from '@radix-ui/react-tooltip';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';

import { classNames } from '../utils/class-names';
import styles from './tooltip.component.module.css';

/** Wrap the app (or a subtree) once so tooltips share timing. */
export const TooltipProvider = RadixTooltip.Provider;

interface TooltipProps extends Omit<
	ComponentPropsWithoutRef<typeof RadixTooltip.Content>,
	'content' | 'children'
> {
	/** The tooltip text/content. */
	content: ReactNode;
	/** The element the tooltip describes (becomes the trigger). */
	children: ReactNode;
}

/**
 * A hover/focus tooltip. Self-wraps a `Provider` so a stray `Tooltip` can't crash
 * without an ancestor one; mount a single `TooltipProvider` near the root to share
 * open/close timing across tooltips (nested providers are fine — the inner wins).
 */
export function Tooltip({ content, children, className, sideOffset = 6, ...props }: TooltipProps) {
	return (
		<RadixTooltip.Provider>
			<RadixTooltip.Root>
				<RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
				<RadixTooltip.Portal>
					<RadixTooltip.Content
						className={classNames(styles.content, className)}
						sideOffset={sideOffset}
						{...props}
					>
						{content}
						<RadixTooltip.Arrow className={styles.arrow} />
					</RadixTooltip.Content>
				</RadixTooltip.Portal>
			</RadixTooltip.Root>
		</RadixTooltip.Provider>
	);
}
